/**
 * Editing operations for hangingProtocolsV2 documents.
 *
 * These live outside the React components on purpose: they are the parts that
 * can silently corrupt a protocol — losing rule identity, dropping configured
 * viewports — and the repo has no jsdom, so component tests cannot cover them.
 */

import { getCombinator, getOperands } from "./conditionDsl";

export const isRuleGroup = (node) => node && typeof node === "object" && Array.isArray(node.rules);

/**
 * Rebuild a group's infix `rules` array from operands plus one combinator.
 * MedDream's UI makes the combinator a per-group toggle, so groups stay uniform.
 */
export function toInfix(operands, combinator) {
  return operands.flatMap((operand, index) =>
    index < operands.length - 1 ? [operand, combinator] : [operand]
  );
}

/**
 * Resize a grid of cells addressed by 1-based `{row, column}`, keeping the cells
 * still in range and creating the rest.
 *
 * Shrinking drops cells outside the new bounds; growing back does NOT bring them
 * back. That is the same behaviour as the v1 editor, and matches the format —
 * there is nowhere to park an off-grid viewport.
 */
export function resizeGrid(cells, rows, columns, create) {
  const byPosition = new Map((cells || []).map((cell) => [`${cell.row}-${cell.column}`, cell]));
  const next = [];
  for (let row = 1; row <= rows; row += 1) {
    for (let column = 1; column <= columns; column += 1) {
      next.push(byPosition.get(`${row}-${column}`) || create({ row, column }));
    }
  }
  return next;
}

/**
 * New id order after dragging `activeId` onto `overId`.
 *
 * Ordering is semantic here — matching is first-applicable-wins — so this
 * produces the full permutation the reorder API requires. A partial list would
 * leave the rest at stale positions and silently change which protocol hangs.
 *
 * Returns null when the drag is a no-op or refers to something not in the list,
 * so callers can skip the round trip.
 */
export function reorderByIds(items, activeId, overId) {
  if (activeId === overId) return null;
  const ids = items.map((item) => item.id);
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from === -1 || to === -1) return null;

  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
}

/**
 * Carry rule `id`s and `path` arrays across from a previous tree onto a freshly
 * parsed one.
 *
 * The text DSL cannot express either: re-parsing mints new uuids and falls back
 * to registry default paths. That matters because the reference export carries
 * per-rule `path` variants for `series.ViewPosition[ANY]` and
 * `series.Laterality[All]` — a bare re-parse would rewrite them, so a round trip
 * through text mode would quietly change the exported file.
 *
 * Operands are matched by position, and only carried over when the rule is
 * otherwise identical, so an edited rule correctly gets fresh identity.
 */
export function reconcileParsedTree(parsed, previous) {
  if (!isRuleGroup(parsed)) return parsed;

  const previousOperands = isRuleGroup(previous) ? getOperands(previous) : [];
  const operands = getOperands(parsed).map((operand, index) => {
    const before = previousOperands[index];
    if (isRuleGroup(operand)) return reconcileParsedTree(operand, before);
    if (!before || isRuleGroup(before)) return operand;

    const unchanged =
      before.field === operand.field &&
      before.operator === operand.operator &&
      String(before.value) === String(operand.value);
    if (!unchanged) return operand;

    return { ...operand, id: before.id, path: [...(before.path || [])] };
  });

  return {
    ...parsed,
    id: isRuleGroup(previous) && previous.id ? previous.id : parsed.id,
    rules: toInfix(operands, getCombinator(parsed)),
  };
}

/* ------------------------------------------------- operand-level editing */

/**
 * Rebuild `group` around a new operand list, keeping its combinator and its own
 * identity. Every operation below funnels through this so no caller can leave a
 * dangling infix separator behind.
 */
function withOperands(group, operands) {
  return { ...group, rules: toInfix(operands, getCombinator(group)) };
}

/**
 * Deep copy with new ids throughout.
 *
 * Duplicating a subtree without reminting ids would give two nodes the same key,
 * which breaks both React reconciliation and drag-and-drop — dnd-kit resolves a
 * drag by id, so the wrong one moves.
 */
export function withFreshIds(node, idFactory) {
  if (!isRuleGroup(node)) {
    return { ...node, id: idFactory(), path: [...(node.path || [])] };
  }
  return {
    ...node,
    id: idFactory(),
    rules: node.rules.map((rule) => (typeof rule === "string" ? rule : withFreshIds(rule, idFactory))),
  };
}

/**
 * Put the operand at `index` inside a new group of its own.
 *
 * This is the operation the old editor had no answer for: a flat group is
 * uniform — all AND or all OR — so mixing them means nesting, and without this
 * the only route to `A && (B || C)` was to add an empty group and retype B and C
 * into it. The new group takes the format's default combinator; with one operand
 * it has no separator to write, so the choice only materialises once a second
 * condition is added.
 */
export function wrapOperandInGroup(group, index, idFactory) {
  const operands = getOperands(group);
  if (index < 0 || index >= operands.length) return group;

  const wrapped = { id: idFactory(), not: false, rules: [operands[index]] };
  return withOperands(
    group,
    operands.map((operand, i) => (i === index ? wrapped : operand))
  );
}

/** Copy the operand at `index` in directly below itself. */
export function duplicateOperand(group, index, idFactory) {
  const operands = getOperands(group);
  if (index < 0 || index >= operands.length) return group;

  const next = [...operands];
  next.splice(index + 1, 0, withFreshIds(operands[index], idFactory));
  return withOperands(group, next);
}

/**
 * Move the operand at `from` to `to`.
 *
 * Order is cosmetic here — unlike protocol order, which decides which protocol
 * hangs — but authors group related conditions together to read them, so it is
 * worth preserving exactly.
 */
export function moveOperand(group, from, to) {
  const operands = getOperands(group);
  if (from === to || from < 0 || to < 0 || from >= operands.length || to >= operands.length) {
    return group;
  }

  const next = [...operands];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return withOperands(group, next);
}

/** Drop the operand at `index`, leaving no dangling separator. */
export function removeOperand(group, index) {
  const operands = getOperands(group);
  if (index < 0 || index >= operands.length) return group;
  return withOperands(
    group,
    operands.filter((_, i) => i !== index)
  );
}
